import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  doc, 
  serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Collection references
const paymentsRef = collection(db, 'payments');
const paymentMethodsRef = collection(db, 'paymentMethods');

// Initialize Firebase Functions
const functions = getFunctions();
const createPaymentIntentFunction = httpsCallable(functions, 'createPaymentIntent');

/**
 * Create a payment intent for a company to pay a student
 * @param {Object} paymentData - Payment data
 * @param {string} paymentData.companyId - Company ID
 * @param {string} paymentData.studentId - Student ID
 * @param {string} paymentData.projectId - Project ID
 * @param {number} paymentData.amount - Amount in cents
 * @param {string} paymentData.currency - Currency code (default: 'usd')
 * @param {string} paymentData.description - Payment description
 * @returns {Promise<Object>} - Payment intent object
 */
export const createPaymentIntent = async (paymentData) => {
  try {
    // Call the Firebase Cloud Function to create a payment intent
    const result = await createPaymentIntentFunction(paymentData);
    
    // Return the payment intent data
    return {
      id: result.data.paymentId,
      clientSecret: result.data.clientSecret,
      ...paymentData
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

/**
 * Get payment methods for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} - Array of payment methods
 */
export const getCompanyPaymentMethods = async (companyId) => {
  try {
    const q = query(paymentMethodsRef, where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting payment methods:', error);
    throw error;
  }
};

/**
 * Add a payment method for a company
 * @param {Object} paymentMethodData - Payment method data
 * @returns {Promise<Object>} - Added payment method
 */
export const addPaymentMethod = async (paymentMethodData) => {
  try {
    const docRef = await addDoc(paymentMethodsRef, {
      ...paymentMethodData,
      createdAt: serverTimestamp()
    });
    
    return {
      id: docRef.id,
      ...paymentMethodData
    };
  } catch (error) {
    console.error('Error adding payment method:', error);
    throw error;
  }
};

/**
 * Get payments by company ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} - Array of payments
 */
export const getCompanyPayments = async (companyId) => {
  try {
    const q = query(paymentsRef, where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting company payments:', error);
    throw error;
  }
};

/**
 * Get payments by student ID
 * @param {string} studentId - Student ID
 * @returns {Promise<Array>} - Array of payments
 */
export const getStudentPayments = async (studentId) => {
  try {
    const q = query(paymentsRef, where('studentId', '==', studentId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting student payments:', error);
    throw error;
  }
};

/**
 * Update payment status
 * @param {string} paymentId - Payment ID
 * @param {string} status - New status
 * @returns {Promise<void>}
 */
export const updatePaymentStatus = async (paymentId, status) => {
  try {
    const paymentRef = doc(db, 'payments', paymentId);
    await updateDoc(paymentRef, {
      status,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
}; 